import type { Buffer as NodeBuffer } from 'buffer';

export type RawBufferInput = ArrayBuffer | ArrayBufferView | NodeBuffer;

const hasNodeBuffer = typeof Buffer !== 'undefined';

const isNodeBuffer = (value: unknown): value is NodeBuffer => {
  return hasNodeBuffer && value instanceof Buffer;
};

const toUint8Array = (value: RawBufferInput): Uint8Array => {
  if (value instanceof Uint8Array) {
    return value;
  }
  if (isNodeBuffer(value)) {
    return new Uint8Array(value.buffer, value.byteOffset, value.byteLength);
  }
  if (ArrayBuffer.isView(value)) {
    const view = value as ArrayBufferView;
    return new Uint8Array(view.buffer, view.byteOffset, view.byteLength);
  }
  if (value instanceof ArrayBuffer) {
    return new Uint8Array(value);
  }
  throw new Error('Unsupported raw buffer input. Expected ArrayBuffer, TypedArray, or Buffer.');
};

export const ensureArrayBuffer = (value: RawBufferInput): ArrayBuffer => {
  const view = toUint8Array(value);
  return view.buffer.slice(view.byteOffset, view.byteOffset + view.byteLength) as ArrayBuffer;
};

export const bufferToBase64 = (value: RawBufferInput): string => {
  const view = toUint8Array(value);
  if (hasNodeBuffer) {
    return Buffer.from(view.buffer, view.byteOffset, view.byteLength).toString('base64');
  }
  let binary = '';
  for (let i = 0; i < view.byteLength; i++) {
    binary += String.fromCharCode(view[i]);
  }
  return btoa(binary);
};

/**
 * Client → server raw frames carry the area they are addressed to in a 1-byte
 * length-prefixed header, because the binary transports (WebRTC data channel /
 * WebSocket) send the payload with no protocol envelope to put an areaId in.
 *
 *     [uint8 areaIdByteLength][areaId utf8 bytes][...original payload]
 *
 * A length of 0 means "no area" — the client is not joined to any area — and
 * the server fans the frame out to every area the client is in, exactly as it
 * did before area routing. The header is ALWAYS present so there is nothing to
 * sniff or guess: every client writes it, every server reads it.
 *
 * Peer→peer raw frames are a different path and are NOT framed.
 */
const RAW_AREA_ID_MAX_BYTES = 255;

export const frameRawWithArea = (value: RawBufferInput, areaId?: string | number): ArrayBuffer => {
  const payload = toUint8Array(value);
  const idBytes = areaId === undefined || areaId === null
    ? new Uint8Array(0)
    : new TextEncoder().encode(String(areaId));
  if (idBytes.byteLength > RAW_AREA_ID_MAX_BYTES) {
    throw new Error(`Area id "${String(areaId)}" is too long to address a raw message (max ${RAW_AREA_ID_MAX_BYTES} bytes).`);
  }
  const framed = new Uint8Array(1 + idBytes.byteLength + payload.byteLength);
  framed[0] = idBytes.byteLength;
  framed.set(idBytes, 1);
  framed.set(payload, 1 + idBytes.byteLength);
  return framed.buffer as ArrayBuffer;
};

export const unframeRawWithArea = (value: RawBufferInput): { areaId?: string, data: ArrayBuffer } => {
  const framed = toUint8Array(value);
  if (framed.byteLength === 0) return { data: new ArrayBuffer(0) };
  const idLength = framed[0];
  const payloadStart = 1 + idLength;
  if (payloadStart > framed.byteLength) {
    throw new Error('Malformed raw message frame: area id length exceeds the frame.');
  }
  const areaId = idLength
    ? new TextDecoder().decode(framed.subarray(1, payloadStart))
    : undefined;
  const payload = framed.subarray(payloadStart);
  return { areaId, data: payload.buffer.slice(payload.byteOffset, payload.byteOffset + payload.byteLength) as ArrayBuffer };
};

export const base64ToArrayBuffer = (value: string): ArrayBuffer => {
  if (hasNodeBuffer) {
    const buffer = Buffer.from(value, 'base64');
    return buffer.buffer.slice(buffer.byteOffset, buffer.byteOffset + buffer.byteLength);
  }
  const binary = atob(value);
  const len = binary.length;
  const view = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    view[i] = binary.charCodeAt(i);
  }
  return view.buffer;
};
